// Engine orders (packages/shared domain/engineOrder.ts): the ticks on the Engine Orders page, kept by SAP.
// The worker routes are in apps/worker/src/routes/engineOrders.ts. A tick shows straight away; ticking or
// unticking Called sales also changes the boat's Engine Install on the Overview, which the page reads, so
// the Overview cache takes the new value as soon as the worker confirms it.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emptyEngineOrder, type EngineOrder } from '@technohull/shared';
import { api } from '../lib/api.js';
import type { OverviewResponse } from './hooks.js';
import { useActiveSeason } from '../season/SeasonContext.js';

export type { EngineOrder };
const KEY = ['engine-orders'] as const;
type Orders = { orders: Record<string, EngineOrder> };

export function useEngineOrders(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<Orders>('/api/engine-orders'),
    enabled,
    staleTime: 15_000,
  });
}

export function useSetEngineOrder(season?: string) {
  const qc = useQueryClient();
  const active = useActiveSeason();
  const s = season ?? active;
  return useMutation({
    mutationFn: (v: { sap: string; calledSales?: boolean; delivered?: boolean }) =>
      api.post<{ ok: true; order: EngineOrder; engineInstall: string | null }>('/api/engine-orders', { ...v, season: s }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<Orders>(KEY);
      qc.setQueryData<Orders>(KEY, (d) => {
        const cur = d?.orders[v.sap] ?? emptyEngineOrder(v.sap);
        const next = {
          ...cur,
          ...(v.calledSales !== undefined ? { calledSales: v.calledSales } : {}),
          ...(v.delivered !== undefined ? { delivered: v.delivered } : {}),
        };
        return { orders: { ...(d?.orders ?? {}), [v.sap]: next } };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(KEY, ctx.prev); },
    onSuccess: (res, v) => {
      qc.setQueryData<Orders>(KEY, (d) => ({ orders: { ...(d?.orders ?? {}), [v.sap]: res.order } }));
      // the Overview's Engine Install followed Called sales
      if (res.engineInstall !== null) {
        qc.setQueryData<OverviewResponse>(['overview', s], (o) =>
          o ? { ...o, vessels: o.vessels.map((b) => (b.sap.trim() === v.sap ? { ...b, engineInstall: res.engineInstall ?? '' } : b)) } : o);
      }
    },
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: KEY });
      if (v.calledSales !== undefined) void qc.invalidateQueries({ queryKey: ['overview', s] });
    },
  });
}
